import { Component, AfterViewInit, OnDestroy, Inject, LOCALE_ID, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { ChatService } from 'src/app/services/chat.service';
import { Theme } from 'src/app/models/theme';
import { TradingViewDataFeed } from 'src/app/services/tradingview-data-feed';
import CryptoCandleService from 'src/app/services/crypto-candle.service';
import { ChartLayoutService } from 'src/app/services/chart-layout.service';

declare var TradingView: any;

@Component({
  selector: 'trading-chart',
  templateUrl: './trading-chart.component.html',
  styleUrls: ['./trading-chart.component.scss'],
  providers: [CryptoCandleService, ChartLayoutService]
})
export class TradingChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() pair: string;
  @Input() mobile = false;
  @Input() fullscreen = false;
  @Input() theme: Theme = null;

  readonly CHART_ID = 'tradingViewChart';
  readonly QUERY_PARAM_MOBILE = 'mobile';
  readonly QUERY_PARAM_FULLSCREEN = 'fullScreen';

  chart: any;

  pairSubscription: Subscription;
  themeSubscription: Subscription;

  constructor(
    private cryptoCandleService: CryptoCandleService,
    private chartLayoutService: ChartLayoutService,
    private chatService: ChatService,
    @Inject(LOCALE_ID) private locale: string
  ) { }

  ngAfterViewInit() {
    this.chatService.destroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const { pair, mobile, fullscreen, theme } = changes;

    if (pair && pair.currentValue !== pair.previousValue) {
      this.drawChart();
    }

    if (mobile && mobile.currentValue !== mobile.previousValue) {
      this.drawChart();
    } else if (fullscreen && fullscreen.currentValue !== fullscreen.previousValue) {
      this.drawChart();
    }

    if (theme && theme.previousValue !== theme.currentValue) {
      this.drawChart();
    }
  }

  ngOnDestroy() {
    this.pairSubscription?.unsubscribe();
    this.themeSubscription?.unsubscribe();
    this.destroyChart();
  }

  fullScreen() {
    window.document.getElementById(this.CHART_ID).requestFullscreen();
  }

  loadChartLayout() {
    const chartLayout = this.chartLayoutService.loadChartLayout();
    const chartPaneState = chartLayout?.charts?.[0].panes?.[0].sources?.[0].state;

    if(!chartPaneState) {
      return;
    }

    chartPaneState.symbol = this.pair;
    chartPaneState.shortName = this.pair;
    this.chart.load(chartLayout);
  }

  saveChartLayout() {
    this.chart.save(data => {
      this.chartLayoutService.saveChartLayout(data);
    });
  }

  drawChart() {
    if (window.document.getElementById(this.CHART_ID) && this.pair && this.theme) {
      this.destroyChart();

      this.chart = new TradingView.widget(this.chartOptions);

      this.chart.subscribe?.('onChartReady', () => {
        this.loadChartLayout();

        if (this.mobile && !this.fullscreen) {
          this.drawFullScreenButton();
        }
      });

      this.chart.subscribe?.('onAutoSaveNeeded', () => {
        this.saveChartLayout();
      });
    }
  }

  destroyChart() {
    if (this.chart) {
      this.chart.unsubscribe('onChartReady');
      this.chart.unsubscribe('onAutoSaveNeeded');
      this.chart = null;
    }
  }

  drawFullScreenButton() {
    const button = this.chart.createButton();
    button.setAttribute('title', 'Switch to full screen');
    button.innerHTML = this.fullScreenButtonHtml;
    button.addEventListener('click', function() {
      // TODO: clean this out

      const globalWindow = window as any;
      // iOS
      if (globalWindow.webkit) {
        globalWindow.webkit.messageHandlers.jsHandler.postMessage('chart_fullscreen');
      }

      // Android
      if (globalWindow.androidBridge) {
        globalWindow.androidBridge.enterFullScreen();
      }
    });
  }

  get chartOptions() {
    return {
      // debug: true, // uncomment this line to see Library errors and warnings in the console
      autosize: true,
      symbol: this.pair,
      datafeed: new TradingViewDataFeed(this.pair, this.cryptoCandleService),
      style: '1',
      timezone: this.timeZone,
      locale: this.locale === 'ar' ? 'ar' : 'en',
      hide_side_toolbar: false,
      enable_publishing: false,
      hide_top_toolbar: true,
      hide_legend: true,
      withdateranges: true,
      container: this.CHART_ID,
      library_path: 'assets/tradingview/charting_library/',
      custom_css_url: this.darkMode ? 'styles/style-dark.css' : 'styles/style-light.css',
      load_last_chart: true,
      widgetbar: {
        datawindow: false,
        details: false,
        watchlist: false,
        watchlist_settings: {
          default_symbols: []
        }
      },
      drawings_access: { type: 'black', tools: [{ name: 'Regression Trend' }] },
      theme: this.darkMode ? 'dark' : 'light',
      overrides: {
        'paneProperties.legendProperties.showStudyArguments': false,
        'paneProperties.legendProperties.showSeriesTitle': false,
        'paneProperties.legendProperties.showSeriesOHLC': true,
        'mainSeriesProperties.candleStyle.upColor': '#40a795',
        'mainSeriesProperties.candleStyle.downColor': '#dd5f68',
        'paneProperties.backgroundType': 'solid',
        'paneProperties.background': this.darkMode ? '#22252d' : '#FFF',
        'paneProperties.vertGridProperties.color': this.darkMode ? '#444' : '#E6E6E6',
        'paneProperties.horzGridProperties.color': this.darkMode ? '#444' : '#E6E6E6',
        'paneProperties.crossHairProperties.color': this.darkMode ? '#444' : '#989898',
        'scalesProperties.lineColor' : '#999',
        'scalesProperties.textColor' : '#999'
      },
      loading_screen: {
        foregroundColor: this.darkMode ? '#22252d' : '#FFF',
        backgroundColor: this.darkMode ? '#22252d' : '#FFF'
      },
      enabled_features: this.chartEnabledFeatures,
      disabled_features: this.chartDisabledFeatures,
      interval: '60',
      time_frames: [
        {
          'text': '1y',
          'resolution': 'D'
        },
        {
          'text': '3m',
          'resolution': '720'
        },
        {
          'text': '1m',
          'resolution': '360'
        },
        {
          'text': '7d',
          'resolution': '60'
        },
        {
          'text': '3d',
          'resolution': '30'
        },
        {
          'text': '1d',
          'resolution': '15'
        }
      ]
    };
  }

  get darkMode() {
    return this.theme === Theme.Dark;
  }

  get chartEnabledFeatures() {
    // Enable for desktop and mobile view
    const features = [];

    // Enable for Mobile View Only
    if (this.mobile && !this.fullscreen) {
      features.push(
        'hide_left_toolbar_by_default'
      );
    }

    return features;
  }

  get chartDisabledFeatures() {
    // Disable for desktop and mobile view
    const features = [
      'header_settings',
      'use_localstorage_for_settings',
      'header_symbol_search',
      'header_interval_dialog_button',
      'header_compare',
      'symbol_info',
      'border_around_the_chart',
      'scales_context_menu',
      'legend_context_menu',
      'edit_buttons_in_legend',
      'go_to_date',
      'chart_property_page_style',
      'study_templates',
      'header_saveload',
      'use_localstorage_for_settings'
    ];

    // Disable for Mobile View Only
    if (this.mobile) {
      features.push(
        'header_undo_redo',
        'header_fullscreen_button',
        'header_screenshot',
        'control_bar',
        'timeframes_toolbar'
      );
    }

    return features;
  }

  get fullScreenButtonHtml() {
    // TODO: make this cleaner
    return '<span class="icon-2Vpz_LXc"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 28" width="28" height="28"><g fill="currentColor"><path d="M21 7v4h1V6h-5v1z"></path><path d="M16.854 11.854l5-5-.708-.708-5 5zM7 7v4H6V6h5v1z"></path><path d="M11.146 11.854l-5-5 .708-.708 5 5zM21 21v-4h1v5h-5v-1z"></path><path d="M16.854 16.146l5 5-.708.708-5-5z"></path><g><path d="M7 21v-4H6v5h5v-1z"></path><path d="M11.146 16.146l-5 5 .708.708 5-5z"></path></g></g></svg></span>';
  }

  get timeZone() {
    const diff = new Date().getTimezoneOffset() * -60;
    return this.timeZones[diff.toString()];
  }

  // tslint:disable-next-line: member-ordering
  timeZones = {
    '-36000': 'Pacific/Honolulu',
    '-25200': 'America/Los_Angeles',
    '-21600': 'US/Mountain',
    '-18000': 'America/Chicago',
    '-14400': 'America/New_York',
    '-10800': 'America/Argentina/Buenos_Aires',
    '3600': 'Europe/London',
    '7200': 'Europe/Paris',
    '10800': 'Asia/Riyadh',
    '14400': 'Asia/Dubai',
    '16200': 'Asia/Tehran',
    '18000': 'Asia/Ashkhabad',
    '19800': 'Asia/Kolkata',
    '20700': 'Asia/Kathmandu',
    '21600': 'Asia/Almaty',
    '25200': 'Asia/Bangkok',
    '28800': 'Asia/Singapore',
    '32400': 'Asia/Tokyo',
    '34200': 'Australia/Adelaide',
    '36000': 'Australia/Sydney',
    '43200': 'Pacific/Auckland',
    '45900': 'Pacific/Chatham',
    '46800': 'Pacific/Fakaofo'
  };
}
